// =====================================================
// BED — Zone administrative (migration 00232)
// =====================================================
// Accès staff aux données du bulletin d'engagement d'un équipage : état,
// éléments manquants, levées manuelles, override de statut, émission du lien
// du FORMULAIRE.
//
// Toutes les RPC portent leur propre garde de rôle côté serveur
// (`superadmin`/`admin`, 42501 sinon) : ce module ne rejoue aucun contrôle
// d'accès, il traduit les erreurs nommées en statut discriminé — comme le reste
// du module BED (cf. `src/lib/bed/draft.ts`).

import { supabase } from '../supabase';

// -----------------------------------------------------------------------
// TYPES
// -----------------------------------------------------------------------

/** Élément manquant tel que la zone admin le voit : celui de 00231 + la levée. */
export interface BedAdminMissingItem {
  code: string;
  /** 00253 : 'custom' = ajouté à la main par le staff (retour 29). */
  kind: 'deferred' | 'empty' | 'custom';
  /** 00253 : id de l'élément personnalisé (kind === 'custom'). */
  item_id?: string | null;
  custom_note?: string | null;
  label: string;
  role: string;
  role_label: string;
  /** Date d'engagement « je communique avant le … » (kind === 'deferred'). */
  due_date?: string | null;
  /** Levé à la main par l'administration. */
  resolved: boolean;
  resolved_at?: string | null;
  resolved_by?: string | null;
  note?: string | null;
}

export interface BedAdminSignature {
  data_url: string;
  signed_at: string;
}

export interface BedAdminCertificateReview {
  status: 'pending' | 'validated' | 'rejected';
  note?: string | null;
  reviewed_at?: string | null;
  reviewed_by?: string | null;
}

export interface BedAdminCertificate {
  path: string;
  name: string;
  size: number;
  uploaded_at?: string;
  /** 00253 (retour 28) : verdict du staff sur ce fichier ('pending' sans verdict). */
  review?: BedAdminCertificateReview;
}

export interface BedAdminMissingSnapshot {
  /** D'où vient la liste : brouillon du lien, dernière soumission, ou rien. */
  source: 'draft' | 'submission' | 'none';
  items: BedAdminMissingItem[];
  /** Ce qui manque ENCORE (levées manuelles déduites). */
  outstanding_count: number;
  resolved_count: number;
  submitted_at: string | null;
  /** `{ "<groupe>:<rôle>": { due_date } }` — engagements « avant le … ». */
  deferred: Record<string, { due_date?: string }>;
  signatures: Partial<Record<'pilot' | 'copilot', BedAdminSignature>>;
  certificates: Partial<Record<'pilot' | 'copilot', BedAdminCertificate>>;
  draft_id: string | null;
  bed_access_token_id: string | null;
}

export interface BedAdminPaymentState {
  exists: boolean;
  settled: boolean;
  /**
   * 00296 : sans dossier, le solde vivant est nul ou négatif (rien à payer).
   * 00305 : avec un dossier, une remise posée après l'émission couvre le total
   * (rien à encaisser, `remaining_cents` 0, `settled` false : le staff finalise).
   */
  nothing_due?: boolean;
  /**
   * 00305 (TICKET-BED-32) : bulletin envoyé sans paiement, puis la réduction ou
   * la grille a changé : un solde est de nouveau dû (`remaining_cents`) et aucun
   * dossier de paiement n'existe. Le staff génère un lien de paiement.
   */
  debt_reopened?: boolean;
  /**
   * 00306 (TICKET-BED-32 Y2) : même dette, mais l'équipage est déjà Inscrit
   * définitif : le solde dû est dit (`remaining_cents`), le statut ne change
   * pas tout seul (aucune relance, rien côté participant).
   */
  debt_after_final_registration?: boolean;
  /**
   * 00305 (TICKET-BED-32) : le total paraît couvert, mais l'acompte retenu n'est
   * pas porté par l'équipage (attestation d'un membre ou d'un contact) : il peut
   * déjà couvrir un autre équipage du même participant. Jamais « réglé ».
   */
  acompte_not_on_crew?: boolean;
  /**
   * 00305 : le total paraît couvert (réduction), mais aucun acompte n'est
   * enregistré (ni BPI payé, ni acompte validé). Jamais « réglé ».
   */
  acompte_missing?: boolean;
  /**
   * 00305 : BPI payé par l'équipage, mais un lien de bulletin de CONTACT du
   * payeur est encore actif et pourrait déduire l'acompte une seconde fois.
   */
  contact_link_active?: boolean;
  payment_case_id?: string;
  status?: string;
  amount_cents?: number;
  paid_cents?: number;
  remaining_cents?: number;
  order_id?: string;
  method?: string | null;
}

export interface BedAdminLink {
  bed_access_token_id: string;
  token: string;
  created_at: string;
  expires_at: string;
  expired: boolean;
}

export interface BedCrewOverview {
  crew_id: string;
  bed_status: string;
  bed_status_label: string;
  /** Statut que le dossier DEVRAIT porter (null si le BED n'est pas engagé). */
  target_status: string | null;
  /** Tout est réglé et complet, mais le passage en Inscrit déf. reste manuel. */
  ready_for_final_registration: boolean;
  missing: BedAdminMissingSnapshot;
  payment: BedAdminPaymentState;
  info_deadline: string | null;
  link: BedAdminLink | null;
}

/** Synthèse BED d'un équipage, pour les listes et l'export CSV. */
export interface BedCrewSummary {
  crew_id: string;
  bed_status: string;
  bed_status_label: string;
  bed_missing_count: number;
  bed_resolved_count: number;
  bed_missing_labels: string;
  bed_submitted_at: string | null;
  bed_payment_status: string | null;
  bed_amount_cents: number | null;
  bed_paid_cents: number | null;
  bed_remaining_cents: number | null;
  bed_payment_settled: boolean;
}

export type BedAdminError =
  | 'certificate_changed'
  | 'forbidden'
  | 'not_found'
  | 'no_paid_bpi_parent'
  // (00242) Plusieurs membres de l'équipage portent une attestation « BPI
  // réglé » de montants DIFFÉRENTS : le serveur refuse de choisir.
  | 'bpi_manual_conflicting_amounts'
  // (00242) Un BPI RÉELLEMENT encaissé couvre déjà l'inscription (dossier porté
  // par un membre) : le lien doit s'appuyer dessus, pas sur l'attestation.
  | 'bpi_paid_on_related_owner'
  // (00307) Le dernier BPI payé de l'équipage porte sur une autre édition que
  // la sienne (report) : le lien n'est pas émis sur cette autre édition.
  | 'bpi_paid_on_other_edition'
  // (00307) L'équipage n'est rattaché à aucune édition (crews.product_id vide).
  | 'crew_without_edition'
  | 'error';

export type BedOverviewResult =
  | { status: 'ok'; data: BedCrewOverview }
  | { status: BedAdminError; message?: string };

export type BedMintResult =
  | {
      status: 'ok';
      token: string;
      url: string;
      expiresInDays: number;
      /**
       * (00242) `true` = le serveur a RENDU le lien actif existant au lieu d'en
       * émettre un nouveau. L'émission est idempotente : régénérer ne détache
       * plus le brouillon déjà saisi ni le certificat déposé.
       */
      reused: boolean;
    }
  | { status: BedAdminError; message?: string };

// -----------------------------------------------------------------------
// MAPPING D'ERREURS
// -----------------------------------------------------------------------

function classify(message: string | undefined): BedAdminError {
  if (!message) return 'error';
  if (message.includes('bed_certificate_changed')) return 'certificate_changed';
  if (message.includes('insufficient_privilege')) return 'forbidden';
  // (00242) Testé AVANT `not_found` : le message porte `bpi_manual_…`, aucun
  // des deux codes n'est un préfixe de l'autre, mais l'ordre reste explicite.
  if (message.includes('bpi_manual_conflicting_amounts')) {
    return 'bpi_manual_conflicting_amounts';
  }
  if (message.includes('bpi_paid_on_related_owner')) return 'bpi_paid_on_related_owner';
  if (message.includes('bed_bpi_paid_on_other_edition')) return 'bpi_paid_on_other_edition';
  if (message.includes('bed_crew_without_edition')) return 'crew_without_edition';
  if (message.includes('no_paid_bpi_parent')) return 'no_paid_bpi_parent';
  if (message.includes('not_found')) return 'not_found';
  return 'error';
}

/** Message actionnable pour l'admin, jamais le message brut de PostgreSQL. */
export function bedAdminErrorMessage(error: BedAdminError): string {
  switch (error) {
    case 'certificate_changed':
      return 'Le certificat a changé. Consultez le nouveau document avant de décider.';
    case 'forbidden':
      return "Vous n'avez pas les droits sur la zone BED.";
    case 'no_paid_bpi_parent':
      return 'Aucun BPI payé ni validé manuellement pour cet équipage : le lien BED ne peut pas être émis.';
    case 'bpi_manual_conflicting_amounts':
      return 'Les validations manuelles des membres portent des montants différents : validez directement l’équipage pour trancher.';
    case 'bpi_paid_on_related_owner':
      return "Un paiement BPI encaissé couvre déjà cette inscription : le lien BED doit s'appuyer sur ce dossier, pas sur la validation manuelle.";
    case 'bpi_paid_on_other_edition':
      return "Le BPI payé de cet équipage est enregistré sur une autre édition. Rattachez son acompte à l'édition de l'équipage avec « Valider le BPI manuellement » (section Paiement de la fiche), puis générez le lien BED.";
    case 'crew_without_edition':
      return "Cet équipage n'est rattaché à aucune édition : renseignez son édition sur la fiche avant de générer le lien BED.";
    case 'not_found':
      return 'Équipage introuvable.';
    default:
      return 'Une erreur est survenue.';
  }
}

// -----------------------------------------------------------------------
// LIEN PUBLIC DU FORMULAIRE
// -----------------------------------------------------------------------

/**
 * Nom de la variable d'environnement qui porte l'origine du SITE 4L Trophy.
 * Exporté pour que le message d'erreur et les tests citent la même chaîne.
 */
export const BED_SITE_ORIGIN_ENV = 'VITE_SITE_4LT_URL';

/** Chemin canonique de l'entrée publique du bulletin, côté site 4LT. */
export const BED_ENGAGEMENT_PATH = '/engagement';

/** Chemin du tunnel de paiement du solde BED, côté site 4LT. */
export const BED_PAYMENT_PATH = '/paiement/bed';

export class BedSiteOriginMissingError extends Error {
  constructor() {
    super(
      `${BED_SITE_ORIGIN_ENV} n'est pas définie : impossible d'émettre un lien BED. ` +
        `L'entrée publique du bulletin est ${BED_ENGAGEMENT_PATH} sur le site 4L Trophy, ` +
        `pas une URL du CRM.`
    );
    this.name = 'BedSiteOriginMissingError';
  }
}

/**
 * Lien du formulaire BED transmis au participant.
 *
 * Décision Victor (review PR #194) : l'entrée publique canonique du bulletin
 * pour 4L 2027 est `/engagement` sur le SITE 4L Trophy — la page qui embarque
 * le formulaire en iframe et route la sortie vers le tunnel de solde. C'est le
 * seul chemin où le `postMessage` fonctionne.
 *
 * On ne peut donc PAS dériver l'URL de `window.location.origin` : le lien
 * copié par l'admin pointerait vers le CRM et ne pourrait structurellement pas
 * déclencher le hand-off. L'origine vient d'une variable d'environnement
 * dédiée, et son absence est une ERREUR EXPLICITE — jamais un repli silencieux
 * sur le CRM (le domaine CRM canonique est crm.desertours.com ; l'ancien
 * `desertourscrm.com` sert un Apache qui renvoie 404 sur /public/forms/*).
 *
 * Le slug n'entre pas dans l'URL : le formulaire est résolu SERVEUR à partir
 * du jeton (get_bed_bootstrap).
 */
export function buildBedFormLink(token: string): string {
  const origin = import.meta.env.VITE_SITE_4LT_URL;
  if (!origin || typeof origin !== 'string' || origin.trim() === '') {
    throw new BedSiteOriginMissingError();
  }
  return `${origin.replace(/\/+$/, '')}${BED_ENGAGEMENT_PATH}?token=${encodeURIComponent(token)}`;
}

// -----------------------------------------------------------------------
// APPELS
// -----------------------------------------------------------------------

/** État complet du bulletin d'engagement d'un équipage (zone admin). */
export async function getCrewBedOverview(crewId: string): Promise<BedOverviewResult> {
  try {
    const { data, error } = await supabase.rpc('get_crew_bed_overview', { p_crew_id: crewId });
    if (error) return { status: classify(error.message), message: error.message };
    return { status: 'ok', data: data as BedCrewOverview };
  } catch (err) {
    console.error('[BED-ADMIN] get_crew_bed_overview:', err);
    return { status: 'error' };
  }
}

export interface BedSoldePreview {
  prix_total_cents: number;
  acompte_cents: number;
  reduction_cents: number;
  /** Options souscrites sur le dossier de solde (0 sans dossier). */
  options_cents: number;
  /** Total vivant, même calcul que bed_sync_case_amount (00295) : solde + options. */
  live_total_cents: number;
}

/**
 * Total calculé en direct pour l'équipage : postes obligatoires − acompte −
 * remise (`get_bed_solde_for_owner`) + options du dossier courant. Null si
 * incalculable (grille non confirmée, prix absent, acompte manuel ambigu) :
 * la carte n'affiche alors rien de plus. Sans preuve de BPI, l'acompte vaut 0.
 */
export async function getCrewBedSoldePreview(
  crewId: string,
  paymentCaseId?: string
): Promise<BedSoldePreview | null> {
  try {
    const { data, error } = await supabase.rpc('get_bed_solde_for_owner', {
      p_crew_id: crewId,
      p_contact_id: null,
    });
    if (error || !data || typeof data.solde_cents !== 'number') return null;

    let optionsCents = 0;
    if (paymentCaseId) {
      const { data: options, error: optionsError } = await supabase
        .from('payment_case_options')
        .select('amount_cents_snapshot')
        .eq('payment_case_id', paymentCaseId);
      if (optionsError) return null;
      optionsCents = (options ?? []).reduce((sum, o) => sum + o.amount_cents_snapshot, 0);
    }

    return {
      prix_total_cents: data.prix_total_cents,
      acompte_cents: data.acompte_cents,
      reduction_cents: data.reduction_cents ?? 0,
      options_cents: optionsCents,
      live_total_cents: data.solde_cents + optionsCents,
    };
  } catch (err) {
    console.error('[BED-ADMIN] get_bed_solde_for_owner:', err);
    return null;
  }
}

/**
 * Lève manuellement un élément manquant. Le serveur trace la levée dans la
 * timeline PUIS recalcule le statut BED : la réponse est déjà l'état d'après.
 */
export async function resolveBedMissingItem(
  crewId: string,
  itemCode: string,
  note?: string
): Promise<BedOverviewResult> {
  try {
    const { data, error } = await supabase.rpc('bed_resolve_missing_item', {
      p_crew_id: crewId,
      p_item_code: itemCode,
      p_note: note ?? null,
    });
    if (error) return { status: classify(error.message), message: error.message };
    return { status: 'ok', data: data as BedCrewOverview };
  } catch (err) {
    console.error('[BED-ADMIN] bed_resolve_missing_item:', err);
    return { status: 'error' };
  }
}

/** Annule une levée : l'élément redevient manquant, le statut redescend. */
export async function unresolveBedMissingItem(
  crewId: string,
  itemCode: string
): Promise<BedOverviewResult> {
  try {
    const { data, error } = await supabase.rpc('bed_unresolve_missing_item', {
      p_crew_id: crewId,
      p_item_code: itemCode,
    });
    if (error) return { status: classify(error.message), message: error.message };
    return { status: 'ok', data: data as BedCrewOverview };
  } catch (err) {
    console.error('[BED-ADMIN] bed_unresolve_missing_item:', err);
    return { status: 'error' };
  }
}

/**
 * Override manuel du statut BED (cahier L15) — et seul chemin vers
 * « Inscrit définitif » (cahier L41 : action administrative).
 */
export async function setCrewBedStatus(
  crewId: string,
  status: string,
  note?: string
): Promise<BedOverviewResult> {
  try {
    const { data, error } = await supabase.rpc('bed_set_crew_status', {
      p_crew_id: crewId,
      p_status: status,
      p_note: note ?? null,
    });
    if (error) return { status: classify(error.message), message: error.message };
    return { status: 'ok', data: data as BedCrewOverview };
  } catch (err) {
    console.error('[BED-ADMIN] bed_set_crew_status:', err);
    return { status: 'error' };
  }
}

/** Émet le lien du FORMULAIRE BED et renvoie l'URL complète à copier. */
export async function mintBedFormLink(crewId: string, expiresInDays = 60): Promise<BedMintResult> {
  try {
    const { data, error } = await supabase.rpc('mint_bed_form_link', {
      p_crew_id: crewId,
      p_expires_in_days: expiresInDays,
    });
    if (error) return { status: classify(error.message), message: error.message };
    const row = data as { token: string; form_slug: string | null; reused?: boolean };
    if (!row?.token || !row.form_slug) {
      return { status: 'error', message: 'bed_form_not_found' };
    }
    return {
      status: 'ok',
      token: row.token,
      url: buildBedFormLink(row.token),
      expiresInDays,
      // (00242) Le serveur peut avoir rendu le lien ACTIF existant.
      reused: row.reused === true,
    };
  } catch (err) {
    // Échec explicite : sans l'origine du site 4LT, on ne fabrique PAS un lien
    // CRM de repli — il serait un cul-de-sac pour le participant.
    if (err instanceof BedSiteOriginMissingError) {
      return { status: 'error', message: err.message };
    }
    console.error('[BED-ADMIN] mint_bed_form_link:', err);
    return { status: 'error' };
  }
}

/**
 * Synthèse BED d'un LOT d'équipages (listes + export CSV). Bornée aux ids
 * demandés côté serveur : jamais un balayage de toute la base.
 */
export async function fetchCrewsBedSummary(
  crewIds: string[]
): Promise<Map<string, BedCrewSummary>> {
  const result = new Map<string, BedCrewSummary>();
  if (crewIds.length === 0) return result;

  // Même découpage que les autres enrichissements de liste : on ne dépasse
  // jamais la taille d'URL/payload confortable pour PostgREST.
  const CHUNK = 500;
  for (let i = 0; i < crewIds.length; i += CHUNK) {
    const chunk = crewIds.slice(i, i + CHUNK);
    const { data, error } = await supabase.rpc('get_crews_bed_summary', { p_crew_ids: chunk });
    if (error) {
      // Un utilisateur non-staff n'a pas la zone BED : l'export doit rester
      // possible, simplement sans les colonnes BED.
      console.warn('[BED-ADMIN] get_crews_bed_summary:', error.message);
      return result;
    }
    for (const row of (data ?? []) as BedCrewSummary[]) {
      result.set(row.crew_id, row);
    }
  }
  return result;
}

/**
 * Lien du TUNNEL de paiement du solde BED, côté site 4L Trophy.
 *
 * Même origine que le bulletin (`VITE_SITE_4LT_URL`) et même règle : absence de
 * la variable = erreur explicite, jamais un lien CRM de repli — ce serait
 * exactement le cul-de-sac que ce correctif ferme.
 *
 * `bedToken` est le jeton de TUNNEL (table `bed_tokens`, 00130), à ne pas
 * confondre avec le jeton du FORMULAIRE (`bed_access_tokens`).
 */
export function buildBedPaymentLink(bedToken: string): string {
  const origin = import.meta.env.VITE_SITE_4LT_URL;
  if (!origin || typeof origin !== 'string' || origin.trim() === '') {
    throw new BedSiteOriginMissingError();
  }
  return `${origin.replace(/\/+$/, '')}${BED_PAYMENT_PATH}?token=${encodeURIComponent(bedToken)}`;
}

// ---------------------------------------------------------------------------
// 00253 — recette 4L : certificats, manquants personnalisés, relance, réponses
// ---------------------------------------------------------------------------

/** Retour 28 : verdict du staff sur le certificat médical d'un rôle. */
export async function reviewBedCertificate(
  crewId: string,
  role: 'pilot' | 'copilot',
  status: 'validated' | 'rejected',
  expectedPath: string,
  note?: string
): Promise<BedOverviewResult> {
  try {
    const { data, error } = await supabase.rpc('bed_review_certificate', {
      p_crew_id: crewId,
      p_role: role,
      p_status: status,
      p_expected_path: expectedPath,
      p_note: note ?? null,
    });
    if (error) return { status: classify(error.message), message: error.message };
    return { status: 'ok', data: data as BedCrewOverview };
  } catch (err) {
    console.error('[BED-ADMIN] bed_review_certificate:', err);
    return { status: 'error' };
  }
}

/** Retour 28 : URL signée (10 min) pour VOIR un certificat du bucket privé. */
export async function getBedCertificateSignedUrl(path: string): Promise<string | null> {
  try {
    const { data, error } = await supabase.storage.from('bed-uploads').createSignedUrl(path, 600);
    if (error || !data?.signedUrl) {
      console.error('[BED-ADMIN] signed url certificat:', error);
      return null;
    }
    return data.signedUrl;
  } catch (err) {
    console.error('[BED-ADMIN] Exception signed url certificat:', err);
    return null;
  }
}

/** Retour 29 : ajoute un élément manquant personnalisé depuis la fiche équipage. */
export async function addBedCustomMissingItem(
  crewId: string,
  label: string,
  note?: string
): Promise<BedOverviewResult> {
  try {
    const { data, error } = await supabase.rpc('bed_add_custom_missing_item', {
      p_crew_id: crewId,
      p_label: label,
      p_note: note ?? null,
    });
    if (error) return { status: classify(error.message), message: error.message };
    return { status: 'ok', data: data as BedCrewOverview };
  } catch (err) {
    console.error('[BED-ADMIN] bed_add_custom_missing_item:', err);
    return { status: 'error' };
  }
}

/** Retour 29 : retire un élément manquant personnalisé (et sa levée éventuelle). */
export async function removeBedCustomMissingItem(
  crewId: string,
  itemId: string
): Promise<BedOverviewResult> {
  try {
    const { data, error } = await supabase.rpc('bed_remove_custom_missing_item', {
      p_crew_id: crewId,
      p_item_id: itemId,
    });
    if (error) return { status: classify(error.message), message: error.message };
    return { status: 'ok', data: data as BedCrewOverview };
  } catch (err) {
    console.error('[BED-ADMIN] bed_remove_custom_missing_item:', err);
    return { status: 'error' };
  }
}

export type BedReminderResult =
  | { status: 'ok'; recipient: string; missingCount: number }
  | { status: 'no_recipient' | 'no_template' | 'no_active_link' }
  | { status: BedAdminError; message?: string };

/** Retour 29 : met en file la relance « informations manquantes » (liste dynamique + lien). */
export async function sendBedMissingReminder(crewId: string): Promise<BedReminderResult> {
  try {
    const { data, error } = await supabase.rpc('bed_send_missing_reminder', { p_crew_id: crewId });
    if (error) return { status: classify(error.message), message: error.message };
    const result = data as {
      enqueued: boolean;
      reason?: string;
      recipient_email?: string;
      missing_count?: number;
    };
    if (!result.enqueued) {
      if (
        result.reason === 'no_template' ||
        result.reason === 'no_recipient' ||
        result.reason === 'no_active_link'
      ) {
        return { status: result.reason };
      }
      return { status: 'error' };
    }
    return {
      status: 'ok',
      recipient: result.recipient_email ?? '',
      missingCount: result.missing_count ?? 0,
    };
  } catch (err) {
    console.error('[BED-ADMIN] bed_send_missing_reminder:', err);
    return { status: 'error' };
  }
}

export interface BedAnswerRow {
  field_id: string;
  step: number;
  order_index: number;
  role: string;
  role_label: string;
  label: string;
  field_type: string;
  value: string;
}

export interface BedAnswersResult {
  found: boolean;
  submitted_at: string | null;
  updated_at: string | null;
  last_step: number | null;
  rows: BedAnswerRow[];
}

/** Retour 30 : toutes les réponses du bulletin (fiche équipage). */
export async function getCrewBedAnswers(
  crewId: string
): Promise<{ status: 'ok'; data: BedAnswersResult } | { status: BedAdminError; message?: string }> {
  try {
    const { data, error } = await supabase.rpc('get_crew_bed_answers', { p_crew_id: crewId });
    if (error) return { status: classify(error.message), message: error.message };
    const raw = data as Partial<BedAnswersResult> | null;
    return {
      status: 'ok',
      data: {
        found: Boolean(raw?.found),
        submitted_at: raw?.submitted_at ?? null,
        updated_at: raw?.updated_at ?? null,
        last_step: raw?.last_step ?? null,
        rows: raw?.rows ?? [],
      },
    };
  } catch (err) {
    console.error('[BED-ADMIN] get_crew_bed_answers:', err);
    return { status: 'error' };
  }
}

export interface BedAnswersExportRow {
  crew_id: string;
  crew_name: string;
  crew_number: string | null;
  status: string;
  status_label: string;
  submitted_at: string | null;
  missing_count: string;
  certificate_pilot: string;
  certificate_copilot: string;
  /** 00292 : choix chambre double Marrakech ('oui' | 'non' | ''). */
  double_room?: string;
  answers: Record<string, string>;
  /** 00271 : rang (`order_index`) de chaque colonne de `answers`. Absent d'un serveur pas encore migré. */
  answer_order?: Record<string, number>;
}

/** Une colonne du modèle d'export : fixe (calculée par le CRM) ou un champ du bulletin, par son rang. */
type BedExportSlot = { fixed: string } | { rank: number };

/** Ordre d'un participant dans le modèle : identité, adresse, études, passé, urgence, passeport, permis, CEAM. */
const PERSON_EXPORT_OFFSETS = [
  20, 10, 70, 30, 40, 50, 60, 80, 100, 110, 120, 130, 135, 140, 150, 160, 500, 510, 520, 600, 610,
  620, 200, 210, 220, 230, 300, 310, 320, 400, 410,
];

/**
 * Modèle de l'équipe Desertours pour l'export des réponses (reçu le 23/09,
 * `modele-csv-export-bed.xlsx`, 80 colonnes). Les champs du bulletin sont
 * désignés par leur rang (`order_index`, 00231 / 00252), jamais par leur
 * libellé : l'équipe peut renommer un champ sans casser l'ordre.
 */
export const BED_EXPORT_LAYOUT: BedExportSlot[] = [
  { fixed: 'Équipage' },
  { fixed: 'N° équipage' },
  { rank: 1010 },
  { fixed: 'Statut' },
  { fixed: 'BED soumis le' },
  { fixed: 'Éléments manquants' },
  ...PERSON_EXPORT_OFFSETS.map((offset) => ({ rank: 2000 + offset })),
  { fixed: 'Certificat pilote validé' },
  ...PERSON_EXPORT_OFFSETS.map((offset) => ({ rank: 3000 + offset })),
  { fixed: 'Certificat copilote validé' },
  // Véhicule : immatriculation, code moteur, date, propriétaire.
  ...[4010, 4030, 4020, 4110, 4120, 4130].map((rank) => ({ rank })),
  // Consentements : règlement, presse, prestataires, données personnelles.
  ...[5010, 5040, 5030, 5020].map((rank) => ({ rank })),
  // 00292 : chambre double Marrakech (métadonnée hors form_fields).
  { fixed: 'Chambre double Marrakech' },
];

/**
 * En-têtes de l'export dans l'ordre du modèle. Une colonne que le modèle ne
 * prévoit pas (champ ajouté au formulaire, adresse de courrier…) n'est jamais
 * perdue : elle suit, par rang puis libellé. Sans rang connu (serveur pas
 * encore migré), elle part à la fin.
 */
export function orderBedExportColumns(ranks: Map<string, number | undefined>): string[] {
  const byRank = new Map<number, string[]>();
  for (const [label, rank] of ranks) {
    if (rank === undefined) continue;
    byRank.set(
      rank,
      [...(byRank.get(rank) ?? []), label].sort((a, b) => a.localeCompare(b))
    );
  }
  const headers: string[] = [];
  const placed = new Set<string>();
  for (const slot of BED_EXPORT_LAYOUT) {
    if ('fixed' in slot) {
      headers.push(slot.fixed);
      continue;
    }
    for (const label of byRank.get(slot.rank) ?? []) {
      headers.push(label);
      placed.add(label);
    }
  }
  const rest = [...ranks.entries()]
    .filter(([label]) => !placed.has(label))
    .sort(
      ([a, ra], [b, rb]) =>
        (ra ?? Number.MAX_SAFE_INTEGER) - (rb ?? Number.MAX_SAFE_INTEGER) || a.localeCompare(b)
    )
    .map(([label]) => label);
  return [...headers, ...rest];
}

const CERT_EXPORT_LABEL: Record<string, string> = {
  validated: 'oui',
  rejected: 'refusé',
  pending: 'en attente',
  absent: 'non',
};

/**
 * Retour 30 : export des réponses BED pour une liste d'équipages, APLATI pour le
 * CSV : colonnes fixes (équipage, statut, soumission, manquants, certificats
 * validés oui/non) puis une colonne par libellé de champ du bulletin.
 */
export async function fetchCrewsBedAnswersExport(
  crewIds: string[]
): Promise<{ status: 'ok'; rows: Record<string, string>[] } | { status: BedAdminError }> {
  const raw: Record<string, string>[] = [];
  const ranks = new Map<string, number | undefined>();
  try {
    for (let i = 0; i < crewIds.length; i += 200) {
      const chunk = crewIds.slice(i, i + 200);
      const { data, error } = await supabase.rpc('get_crews_bed_answers_export', {
        p_crew_ids: chunk,
      });
      if (error) return { status: classify(error.message) };
      for (const r of (data as BedAnswersExportRow[]) ?? []) {
        for (const label of Object.keys(r.answers ?? {})) {
          const rank = r.answer_order?.[label];
          if (!ranks.has(label) || (ranks.get(label) === undefined && rank !== undefined)) {
            ranks.set(label, rank);
          }
        }
        raw.push({
          Équipage: r.crew_name ?? '',
          'N° équipage': r.crew_number ?? '',
          Statut: r.status_label ?? r.status ?? '',
          'BED soumis le': r.submitted_at ? r.submitted_at.slice(0, 10) : '',
          'Éléments manquants': r.missing_count ?? '',
          'Certificat pilote validé': CERT_EXPORT_LABEL[r.certificate_pilot] ?? r.certificate_pilot,
          'Certificat copilote validé':
            CERT_EXPORT_LABEL[r.certificate_copilot] ?? r.certificate_copilot,
          'Chambre double Marrakech': r.double_room ?? '',
          ...(r.answers ?? {}),
        });
      }
    }
    // Ordre du modèle de l'équipe ; toutes les lignes portent toutes les
    // colonnes (CSV rectangulaire), insérées dans cet ordre.
    const headers = orderBedExportColumns(ranks);
    const rows = raw.map((row) => Object.fromEntries(headers.map((h) => [h, row[h] ?? ''])));
    return { status: 'ok', rows };
  } catch (err) {
    console.error('[BED-ADMIN] get_crews_bed_answers_export:', err);
    return { status: 'error' };
  }
}

// -----------------------------------------------------------------------
// DÉROGATION D'ÂGE (TICKET-DEROG-04, blocage provisoire, 00310)
// -----------------------------------------------------------------------

/** Une personne citée par la règle de dérogation (membre ou copilote déclaré). */
export interface BedDerogationPerson {
  contact_id: string | null;
  /** `pilot`, `copilot`, ou `member` (membre sans rôle). */
  role: string;
  /** `member` : carte du membre ; `bulletin` : copilote saisi, pas encore membre. */
  source?: 'member' | 'bulletin';
  first_name: string | null;
  last_name: string | null;
}

/** Pourquoi le paiement du solde BED est bloqué (règle relue en direct). */
export interface BedDerogationBlock {
  blocked: boolean;
  /** 29 ans ou plus au départ, sans « Dérogation validée ». */
  blockers: BedDerogationPerson[];
  /** Âge impossible à contrôler : signalé, ne bloque pas. */
  birth_date_missing: BedDerogationPerson[];
  event_day: string | null;
}

/**
 * Raison du blocage du solde pour la fiche équipage. `null` sur toute erreur
 * (RPC absente avant la migration, droits) : la fiche n'affiche rien de plus,
 * les gardes serveur bloquent le paiement de toute façon.
 */
export async function getCrewDerogationBlock(crewId: string): Promise<BedDerogationBlock | null> {
  try {
    const { data, error } = await supabase.rpc('get_crew_derogation_block', {
      p_crew_id: crewId,
    });
    if (error || !data) {
      if (error) console.warn('[BED-ADMIN] get_crew_derogation_block:', error.message);
      return null;
    }
    return data as BedDerogationBlock;
  } catch (err) {
    console.warn('[BED-ADMIN] get_crew_derogation_block:', err);
    return null;
  }
}
